import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WorkOrder from "../../models/workOrder";
import formHelper from "../../util/formHelper";
import Select from "react-select";
import SelectOption from "../../components/common/selectedOption";
import SubscriptionContextModel from "../../contexts/subscriptionContextModel";
import SubscriptionContext from "../../contexts/subscriptionContext";
import Trade from "../../models/trade";
import tradeService from "../../services/tradeService";
import WorkOrderType from "../../models/workOrderType";
import workOrderTypeHelper from "../../models/workOrderTypeHelper";
import DatePicker from "react-datepicker";
import WorkOrderPriority from "../../models/workOrderPriority";
import workOrderPriorityHelper from "../../models/workOrderPriorityHelper";
import workOrderService from "../../services/workOrderService";
import ServiceProvider from "../../models/serviceProvider";
import serviceProviderService from "../../services/serviceProviderService";
import WorkOrderFileList from "../../components/work/work-order/WorkOrderFileList";
import File from "../../models/file";
import Page from "../../components/work/layout/Page";

const WorkOrderDispatch: React.FC = () => {
  const { id: idParam } = useParams();
  const navigate = useNavigate();

  const { subscription } =
    useContext<SubscriptionContextModel>(SubscriptionContext);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [workOrder, setWorkOrder] = useState<WorkOrder>(new WorkOrder());

  const [tradeOptions, setTradeOptions] = useState<SelectOption<Trade>[]>([]);
  const [selectedTradeOption, setSelectedTradeOption] =
    useState<SelectOption<Trade> | null>(null);

  const [workOrderTypeOptions, setWorkOrderTypeOptions] = useState<
    SelectOption<WorkOrderType>[]
  >([]);
  const [selectedWorkOrderTypeOption, setSelectedWorkOrderTypeOption] =
    useState<SelectOption<WorkOrderType> | null>(null);

  const [workOrderPriorityOptions, setWorkOrderPriorityOptions] = useState<
    SelectOption<WorkOrderPriority>[]
  >([]);
  const [selectedWorkOrderPriotityOption, setSelectedWorkOrderPriotityOption] =
    useState<SelectOption<WorkOrderPriority> | null>(null);

  const [originalDneAmount, setOriginalDneAmount] = useState<number>(0);

  const [serviceProviderOptions, setServiceProviderOptions] = useState<
    SelectOption<ServiceProvider>[]
  >([]);
  const [selectedServiceProviderOptions, setSelectedServiceProviderOptions] =
    useState<SelectOption<ServiceProvider>[]>([]);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!idParam) {
      return;
    }

    const init = async (workOrderNumber: string) => {
      const { data: workOrder } = await workOrderService.getWorkOrderByNumber(
        subscription.idSubscription,
        workOrderNumber,
      );

      if (!workOrder) {
        // TODO: show no work order found page or smth
        return;
      }

      setWorkOrder(workOrder);

      setOriginalDneAmount(workOrder.dneAmount);

      const { data: serviceProviders } =
        await serviceProviderService.getServiceProviders(
          subscription.idSubscription,
        );

      const serviceProviderOptions: SelectOption<ServiceProvider>[] = [];

      for (const serviceProvider of serviceProviders) {
        if (
          serviceProvider.idServiceProvider === workOrder.idToServiceProvider
        ) {
          continue;
        }

        serviceProviderOptions.push({
          value: serviceProvider,
          label: ServiceProvider.getName(serviceProvider),
        });
      }

      setServiceProviderOptions(serviceProviderOptions);

      const tradeOptions: SelectOption<Trade>[] = [];

      const { data: trades } = await tradeService.getTrades();

      for (const trade of trades) {
        tradeOptions.push({
          value: trade,
          label: trade.tradeName,
        });
      }

      setTradeOptions(tradeOptions);

      const selectedTradeOption =
        tradeOptions.find((t) => t.value.idTrade === workOrder.idTrade) ?? null;
      setSelectedTradeOption(selectedTradeOption);

      const workOrderTypeOptions: SelectOption<WorkOrderType>[] = [];

      for (const member in WorkOrderType) {
        const enumMember = member as WorkOrderType;

        if (enumMember === WorkOrderType.None) {
          continue;
        }
        workOrderTypeOptions.push({
          value: enumMember,
          label: workOrderTypeHelper.getDisplayValue(enumMember),
        });
      }

      setWorkOrderTypeOptions(workOrderTypeOptions);

      const selectedWorkWorderTypeOption =
        workOrderTypeOptions.find(
          (wot) => wot.value === workOrder.workOrderType,
        ) ?? null;
      setSelectedWorkOrderTypeOption(selectedWorkWorderTypeOption);

      const workOrderPriorityOptions: SelectOption<WorkOrderPriority>[] = [];

      for (const member in WorkOrderPriority) {
        const enumMember = member as WorkOrderPriority;

        if (enumMember === WorkOrderPriority.None) {
          continue;
        }
        workOrderPriorityOptions.push({
          value: enumMember,
          label: workOrderPriorityHelper.getDisplayValue(enumMember),
        });
      }

      setWorkOrderPriorityOptions(workOrderPriorityOptions);

      const workOrderPriorityOption =
        workOrderPriorityOptions.find(
          (p) => p.value === workOrder.workOrderPriority,
        ) ?? null;
      setSelectedWorkOrderPriotityOption(workOrderPriorityOption);
    };

    init(idParam);
  }, [idParam, subscription]);

  const onTradeChanged = (event: any) => {
    const tradeOption: SelectOption<Trade> = event;
    setSelectedTradeOption(tradeOption);

    const { value: trade } = tradeOption;

    const newWorkOrder = {
      ...workOrder,
      idTrade: trade.idTrade,
      trade: trade,
    };
    setWorkOrder(newWorkOrder);
  };

  const onWorkOrderTypeChanged = (event: any) => {
    const workOrderTypeOption: SelectOption<WorkOrderType> = event;
    setSelectedWorkOrderTypeOption(workOrderTypeOption);

    const { value: workOrderType } = workOrderTypeOption;

    const newWorkOrder = {
      ...workOrder,
      workOrderType: workOrderType,
    };
    setWorkOrder(newWorkOrder);
  };

  const onDneChanged = (event: any) => {
    const newValue = event.target.value;
    const dne = Number(newValue);
    const newWorkOrder = {
      ...workOrder,
      dneAmount: dne,
    };
    setWorkOrder(newWorkOrder);
  };

  const onScheduledDateChanged = (date: Date) => {
    const newWorkOrder = {
      ...workOrder,
      scheduledDate: date,
    };
    setWorkOrder(newWorkOrder);
  };

  const onDueDateChanged = (date: Date) => {
    const newWorkOrder = {
      ...workOrder,
      dueDate: date,
    };
    setWorkOrder(newWorkOrder);
  };

  const onWorkOrderPriorityChanged = (event: any) => {
    const workOrderPriorityOption: SelectOption<WorkOrderPriority> = event;
    setSelectedWorkOrderPriotityOption(workOrderPriorityOption);

    const { value: workOrderPriority } = workOrderPriorityOption;

    const newWorkOrder = {
      ...workOrder,
      workOrderPriority: workOrderPriority,
    };
    setWorkOrder(newWorkOrder);
  };

  const onScopeOfWorkChanged = (event: any) => {
    const newValue = event.target.value;

    const newWorkOrder = {
      ...workOrder,
      scopeOfWork: newValue,
    };
    setWorkOrder(newWorkOrder);
  };

  const onSelectedServiceProviderOptionsChanged = async (options: []) => {
    setSelectedServiceProviderOptions(options);
  };

  const onSubmit = async (event: any) => {
    event.preventDefault();
    setErrorMessage("");

    const serviceProviders = selectedServiceProviderOptions.map(
      (spo) => spo.value,
    );

    setIsBusy(true);

    await workOrderService.dispatchWorkOrder(
      subscription.idSubscription,
      workOrder,
      serviceProviders,
      selectedFiles,
    );

    setIsBusy(false);

    navigate(`/work/work-orders/${workOrder.workOrderNumber}`);
  };

  const onCancel = () => {
    navigate(`/work/work-orders/${workOrder.workOrderNumber}`);
  };

  return (
    <Page
      pageTitle={`Dispatch Work Order # ${workOrder.workOrderNumber}`}
      breadcrumbs={[
        { linkName: "Work Orders", linkUlr: "/work/work-orders" },
        {
          linkName: workOrder.workOrderNumber,
          linkUlr: `/work/work-orders/${workOrder.workOrderNumber}`,
        },
        { linkName: "Dispatch", linkUlr: "" },
      ]}
    >
      <section className="section">
        <div className="row">
          <div className="col-lg">
            <div className="card p-4">
              <form
                className="row g-3"
                onSubmit={onSubmit}
                onKeyDown={formHelper.onEnterKeyDownPreventSubmit}
              >
                <div className="col-12">
                  Account: <b>{workOrder.account.accountName}</b>{" "}
                </div>
                <div className="col-12">
                  Location: <b>{workOrder.location.locationName}</b>
                </div>
                <div className="col-12">
                  <label htmlFor="workOrderType" className="form-label">
                    Work Order Type
                  </label>
                  <Select
                    placeholder="Please select"
                    classNamePrefix="select"
                    id="workOrderType"
                    options={workOrderTypeOptions}
                    value={selectedWorkOrderTypeOption}
                    onChange={onWorkOrderTypeChanged}
                  />
                </div>
                <div className="col-3">
                  <label htmlFor="workOrderPriority" className="form-label">
                    Priority
                  </label>
                  <Select
                    placeholder="Please select"
                    classNamePrefix="select"
                    id="workOrderPriority"
                    options={workOrderPriorityOptions}
                    value={selectedWorkOrderPriotityOption}
                    onChange={onWorkOrderPriorityChanged}
                  />
                </div>
                <div className="col-3">
                  <label htmlFor="dueDate" className="form-label">
                    Due Date
                  </label>
                  <div className="calendar-textbox-full-width">
                    <DatePicker
                      placeholderText="Please select"
                      className="form-control"
                      id="dueDate"
                      selected={workOrder.dueDate}
                      onChange={(date: Date) => onDueDateChanged(date)}
                      popperPlacement="left"
                      minDate={new Date()}
                    />
                  </div>
                </div>
                <div className="col-5">
                  <label htmlFor="dne" className="form-label">
                    DNE
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="dne"
                    value={workOrder.dneAmount ?? ""}
                    onChange={onDneChanged}
                  />
                </div>
                <div className="col-1">
                  <label htmlFor="dne" className="form-label">
                    Original DNE
                  </label>
                  <div>{originalDneAmount}</div>
                </div>
                <div className="col-12">
                  <label htmlFor="referenceNumber" className="form-label">
                    Scope of Work
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Details of the problem or any other instructions"
                    id="referenceNumber"
                    value={workOrder.scopeOfWork ?? ""}
                    onChange={onScopeOfWorkChanged}
                    style={{ height: "100px" }}
                  />
                </div>
                <div className="col-6">
                  <label
                    htmlFor="selectedServiceProviders"
                    className="form-label"
                  >
                    Select Service Providers
                  </label>
                  <div>
                    <Select
                      placeholder="Please select"
                      id="selectedServiceProviders"
                      options={serviceProviderOptions}
                      value={selectedServiceProviderOptions}
                      isMulti
                      onChange={(options: any) => {
                        onSelectedServiceProviderOptionsChanged(options);
                      }}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <label htmlFor="trade" className="form-label">
                    Trade
                  </label>
                  <Select
                    placeholder="Please select"
                    classNamePrefix="select"
                    id="trade"
                    options={tradeOptions}
                    value={selectedTradeOption}
                    onChange={onTradeChanged}
                  />
                </div>
                <div className="col-6">
                  <label htmlFor="scheduledDate" className="form-label">
                    Scheduled Date
                  </label>
                  <div className="calendar-textbox-full-width">
                    <DatePicker
                      className="form-control"
                      placeholderText="Please select"
                      id="scheduledDate"
                      selected={workOrder.scheduledDate}
                      onChange={(date: Date) => onScheduledDateChanged(date)}
                      popperPlacement="bottom-start"
                      minDate={new Date()}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <label htmlFor="scheduledTime" className="form-label">
                    Scheduled Time
                  </label>
                  <div className="calendar-textbox-full-width">
                    <DatePicker
                      className="form-control"
                      placeholderText="Please select"
                      id="scheduledTime"
                      selected={workOrder.scheduledDate}
                      onChange={(date: Date) => onScheduledDateChanged(date)}
                      popperPlacement="bottom-start"
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                    />
                  </div>
                </div>
                <div>
                  <WorkOrderFileList
                    idSubscription={subscription.idSubscription}
                    workOrder={workOrder}
                    hasShare={false}
                    hasDelete={false}
                    onSelectedFilesChanged={(files: File[]) =>
                      setSelectedFiles(files)
                    }
                  />
                </div>
                <div className="col-12">
                  <div className="d-flex justify-content-between">
                    <div>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isBusy}
                      >
                        Dispatch
                      </button>{" "}
                      <button
                        type="reset"
                        className="btn btn-secondary"
                        onClick={() => onCancel()}
                      >
                        Cancel
                      </button>
                    </div>
                    {/* {!isNew && (
                      <div>
                        <button
                          type="reset"
                          className="btn btn-danger"
                          onClick={() => onDeleteConfirm()}
                          disabled={isBusy}
                        >
                          Delete
                        </button>
                      </div>
                    )} */}
                  </div>
                </div>

                {errorMessage !== "" && (
                  <div className="row text-center">
                    <div className="error-message">{errorMessage}</div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
};

export default WorkOrderDispatch;
